import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  deleteTopic, 
  changeTopic, 
  addTopic, 
  removeTopicFromBasket, 
  setBasketContentChanged, 
  setCurrentBasket,
  fetchBasketById,
  selectCurrentBasket,
  updateBasket,
  updateTopic,
} from '../../store/workspacesSlice';
import { removeTopicFromCurrentBasket } from '../../store/basketSlice';
import EditTopicModal from './EditTopicModal';
import MoveTopicModal from './MoveTopicModal';
import TopicSelectionModal from '../TopicSelectionModal/TopicSelectionModal';
import TopicItem from './TopicItem';
import BasketHeader from './BasketHeader';
import AddTopicButton from './AddTopicButton';
import EditBasketModal from '../WorkspaceDetail/EditBasketModal';
import { toast } from 'react-toastify';
import AddItemModal from '../AddItemModal/AddItemModal';
import OfflineStatusBanner from '../OfflineStatusBanner/OfflineStatusBanner';
import RelationDescriptionModal from './RelationDescriptionModal';
import BasketStats from './BasketStats';
import MasteryCelebration from './MasteryCelebration';

const BasketContent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: basketId } = useParams();
  const location = useLocation();
  const { basket: initialBasket, workspaceId, workspaceName, fromWorkspace } = location.state || {};

  const effectiveBasketId = basketId || initialBasket?.id;
  
  const [hasChanges, setHasChanges] = useState(false);
  const currentBasket = useSelector(selectCurrentBasket);

  // Memoize these values to prevent unnecessary re-renders
  const memoizedWorkspaceId = useMemo(() => workspaceId, [workspaceId]);
  const memoizedWorkspaceName = useMemo(() => workspaceName, [workspaceName]);

  useEffect(() => {
    if (effectiveBasketId && !currentBasket) {
      dispatch(fetchBasketById(effectiveBasketId))
        .then(() => {
          dispatch(setCurrentBasket(effectiveBasketId));
        })
        .catch((error) => {
          console.error('Failed to fetch basket:', error);
        });
    }
  }, [dispatch, effectiveBasketId, currentBasket]);

  const basketTopics = useMemo(() => currentBasket ? currentBasket.topics : [], [currentBasket]);

  // State declarations
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicSelectionModalOpen, setTopicSelectionModalOpen] = useState(false);
  const [editBasketModalOpen, setEditBasketModalOpen] = useState(false);
  const [showRelationModal, setShowRelationModal] = useState(false);
  const [showMasteryCelebration, setShowMasteryCelebration] = useState(false);
  const [previousMasteryPercentage, setPreviousMasteryPercentage] = useState(null);
  const [celebrationTimeout, setCelebrationTimeout] = useState(null);

  // Handler for removing a topic from the basket
  const handleRemoveTopicFromBasket = useCallback(async (topicId) => {
    try {
      await dispatch(removeTopicFromBasket({ topicId, basketId: effectiveBasketId, workspaceId: memoizedWorkspaceId })).unwrap();
    } catch (error) {
      console.error('Failed to remove topic from basket:', error);
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId]);

  // Handler for moving a topic to another basket
  const handleMoveTopic = useCallback(async (topicId, newBasketId) => {
    await dispatch(changeTopic({ topicId, newBasketId, workspaceId: memoizedWorkspaceId }));
    dispatch(setBasketContentChanged(true));
    setMoveModalOpen(false);
  }, [dispatch, memoizedWorkspaceId]);

  // Handler for editing a topic
  const handleEditTopic = useCallback((topic) => {
    if (topic.showRelationTab) {
      setSelectedTopic(topic);
      setShowRelationModal(true);
    } else {
      setSelectedTopic(topic);
      setEditModalOpen(true);
    }
  }, []);

  // Handle adding a completely new topic
  const handleAddNewTopic = useCallback(async (newTopicData) => {
    try {
      const topicPayload = newTopicData.workspaceId ? 
        newTopicData : 
        {
          workspaceId: memoizedWorkspaceId,
          newTopic: {
            ...newTopicData,
            workspace_id: memoizedWorkspaceId,
            basket_id: effectiveBasketId
          }
        };

      const result = await dispatch(addTopic(topicPayload)).unwrap();

      if (result) {
        if (currentBasket) {
          dispatch(setCurrentBasket({
            ...currentBasket,
            topics: [...currentBasket.topics, result],
            updated_at: new Date().toISOString()
          }));
        }
        setHasChanges(true);
        dispatch(setBasketContentChanged(true));

        navigate(`/workspace/${memoizedWorkspaceId}`, {
          state: { 
            workspaceName: memoizedWorkspaceName,
            activeTab: 'Baskets',
            hasChanges: true,
            updatedBasketId: effectiveBasketId
          }
        });
      }
    } catch (error) {
      console.error('Error creating new topic:', error);
      toast.error('Failed to create new topic');
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId, currentBasket, navigate, memoizedWorkspaceName]);

  // Handle adding existing topics
  const handleAddExistingTopics = useCallback(async (selectedTopics) => {
    const updatedTopics = [];
    
    for (const topic of selectedTopics) {
      try {
        const result = await dispatch(changeTopic({ 
          topicId: topic.id, 
          newBasketId: effectiveBasketId, 
          workspaceId: memoizedWorkspaceId 
        })).unwrap();
        
        if (result) updatedTopics.push(result);
      } catch (error) {
        console.error('Error moving topic:', error);
      }
    }

    if (updatedTopics.length > 0) {
      dispatch(setCurrentBasket({
        ...currentBasket,
        topics: [...currentBasket.topics, ...updatedTopics]
      }));
      setHasChanges(true);
      dispatch(setBasketContentChanged(true));
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId, currentBasket]);

  // Handler for navigating back to the workspace view
  const handleBackToWorkspace = useCallback(() => {
    navigate(`/workspace/${memoizedWorkspaceId}`, {
      state: { 
        workspaceName: memoizedWorkspaceName,
        activeTab: 'Baskets', 
        hasChanges
      }
    });
  }, [navigate, memoizedWorkspaceId, memoizedWorkspaceName, hasChanges]);

  // Handler for deleting the basket
  const handleDeleteBasket = useCallback(() => {
    // Implement delete logic here
    console.log('Delete basket');
  }, []);

  // Handler for editing the basket
  const handleEditBasket = useCallback(async (updatedData) => {
    try {
      const result = await dispatch(updateBasket({ 
        basketId: effectiveBasketId, 
        updatedData, 
        workspaceId: memoizedWorkspaceId 
      })).unwrap();
      
      if (result) {
        dispatch(setCurrentBasket(result));
        toast.success('Basket updated successfully');
        setEditBasketModalOpen(false);
      }
    } catch (error) {
      console.error('Error updating basket:', error);
      toast.error(`Failed to update basket: ${error.message}`);
    }
  }, [dispatch, effectiveBasketId, memoizedWorkspaceId]);

  const handleSaveRelation = async (updatedTopic) => {
    try {
      const result = await dispatch(updateTopic({
        topicId: updatedTopic.id,
        updates: { relation_description: updatedTopic.relation_description }
      })).unwrap();
      
      if (result) {
        if (currentBasket && currentBasket.topics) {
          const updatedTopics = currentBasket.topics.map(topic =>
            topic.id === result.id ? result : topic
          );
          dispatch(setCurrentBasket({
            ...currentBasket,
            topics: updatedTopics
          }));
        }
        
        setShowRelationModal(false);
        setSelectedTopic(null);
        toast.success('Relationship description updated');
      }
    } catch (error) {
      console.error('Failed to update relationship:', error);
      toast.error('Failed to update relationship');
    }
  };

  const handleToggleMastery = useCallback(async (topicId, isMaster) => {
    try {
      const result = await dispatch(updateTopic({
        topicId,
        updates: { is_master: isMaster },
        workspaceId: memoizedWorkspaceId
      })).unwrap();
      
      if (result && result.topic) {
        if (currentBasket && currentBasket.topics) {
          const updatedTopics = currentBasket.topics.map(topic =>
            topic.id === result.topic.id ? result.topic : topic
          );
          
          // Check if this update results in 100% mastery
          const totalTopics = updatedTopics.length;
          const masteredTopics = updatedTopics.filter(topic => topic.is_master).length;
          const newMasteryPercentage = Math.round((masteredTopics / totalTopics) * 100);
          
          dispatch(setCurrentBasket({
            ...currentBasket,
            topics: updatedTopics
          }));
          
          // Remove the toast notification
          // toast.success(isMaster ? 'Topic marked as mastered' : 'Mastery status removed');
        }
      }
    } catch (error) {
      console.error('Failed to update mastery status:', error);
      toast.error('Failed to update mastery status');
    }
  }, [dispatch, currentBasket, memoizedWorkspaceId]);

  // Modify the useEffect to add a delay before showing the celebration
  useEffect(() => {
    if (!currentBasket || !currentBasket.topics || currentBasket.topics.length === 0) {
      return;
    }
    
    const totalTopics = currentBasket.topics.length;
    const masteredTopics = currentBasket.topics.filter(topic => topic.is_master).length;
    const masteryPercentage = Math.round((masteredTopics / totalTopics) * 100);
    
    // Only show celebration when mastery changes from <100% to 100%
    if (previousMasteryPercentage !== null && 
        previousMasteryPercentage < 100 && 
        masteryPercentage === 100) {
      
      // Clear any existing timeout
      if (celebrationTimeout) {
        clearTimeout(celebrationTimeout);
      }
      
      // Set a 1-second delay before showing the celebration
      const timeout = setTimeout(() => {
        setShowMasteryCelebration(true);
      }, 1000); // 1 seconds delay
      
      setCelebrationTimeout(timeout);
    }
    
    setPreviousMasteryPercentage(masteryPercentage);
    
    // Cleanup function to clear timeout if component unmounts
    return () => {
      if (celebrationTimeout) {
        clearTimeout(celebrationTimeout);
      }
    };
  }, [currentBasket, previousMasteryPercentage, celebrationTimeout]);

  // Render loading state, error message, or basket content
  if (!currentBasket) {
    return (
      <div className="text-center py-8">
        <p className="text-xl font-semibold mb-4">Loading basket data...</p>
      </div>
    );
  }

  return (
    <div className="bg-neutral-50 min-h-screen pb-24 sm:pb-20">
      <BasketHeader 
        onBack={handleBackToWorkspace} 
        title={currentBasket?.name || 'Loading...'}
        description={currentBasket?.description}
        onEdit={() => setEditBasketModalOpen(true)}
        onDelete={handleDeleteBasket}
      />
      
      <OfflineStatusBanner />
      
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <BasketStats basket={currentBasket} />

        {basketTopics.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {basketTopics.map(topic => (
              <TopicItem
                key={topic.id}
                topic={topic}
                onRemove={handleRemoveTopicFromBasket}
                onEdit={handleEditTopic}
                onMove={() => {
                  setSelectedTopic(topic);
                  setMoveModalOpen(true);
                }}
                onToggleMastery={handleToggleMastery}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8 mb-16 sm:mb-20">
            <p className="text-lg sm:text-xl font-semibold text-neutral-900">
              No topics available in this basket.
            </p>
          </div>
        )}

        <div className="fixed bottom-0 left-0 right-0 p-3 sm:p-4 bg-white border-t-2 border-neutral-200">
          <div className="max-w-sm mx-auto">
            <AddTopicButton 
              onClick={() => currentBasket && setTopicSelectionModalOpen(true)} 
              disabled={!currentBasket}
              className="w-full"
            />
          </div>
        </div>

        {topicSelectionModalOpen && currentBasket && (
          <TopicSelectionModal
            onSelect={handleAddExistingTopics}
            onAddNew={handleAddNewTopic}
            onClose={() => setTopicSelectionModalOpen(false)}
            currentBasket={currentBasket}
            currentWorkspaceId={memoizedWorkspaceId}
          />
        )}

        {editBasketModalOpen && currentBasket && (
          <EditBasketModal
            basket={currentBasket}
            onClose={() => setEditBasketModalOpen(false)}
            onSave={handleEditBasket}
          />
        )}

        {showRelationModal && (
          <RelationDescriptionModal
            isOpen={showRelationModal}
            onClose={() => {
              setShowRelationModal(false);
              setSelectedTopic(null);
            }}
            onSave={handleSaveRelation}
            topic={selectedTopic}
          />
        )}

        {showMasteryCelebration && (
          <MasteryCelebration 
            onClose={() => setShowMasteryCelebration(false)} 
            basket={currentBasket}
          />
        )}
      </div>
    </div>
  );
};

export default BasketContent;
